import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faQuestionCircle,
  faTrashAlt,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';

/**
 * Styled confirmation modal to replace browser's window.confirm()
 * Supports different types: confirm, warning, danger, info
 */
const ConfirmModal = ({
  isOpen,
  onConfirm,
  onCancel,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'confirm' // 'confirm' | 'warning' | 'danger' | 'info'
}) => {
  const modalRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const previouslyFocused = useRef(null);

  // Icon and color configurations based on type - using brand colors
  const typeConfig = {
    confirm: {
      icon: faQuestionCircle,
      iconColor: 'text-brand-teal',
      iconBg: 'bg-teal-50',
      confirmBg: 'bg-brand-teal hover:brightness-110',
      confirmText: 'text-white'
    },
    warning: {
      icon: faExclamationTriangle,
      iconColor: 'text-brand-purple',
      iconBg: 'bg-purple-50',
      confirmBg: 'bg-brand-purple hover:brightness-110',
      confirmText: 'text-white'
    },
    danger: {
      icon: faTrashAlt,
      iconColor: 'text-red-600',
      iconBg: 'bg-red-50',
      confirmBg: 'bg-red-600 hover:bg-red-700',
      confirmText: 'text-white'
    },
    info: {
      icon: faInfoCircle,
      iconColor: 'text-brand-teal',
      iconBg: 'bg-teal-50',
      confirmBg: 'bg-brand-teal hover:brightness-110',
      confirmText: 'text-white'
    }
  };

  const config = typeConfig[type] || typeConfig.confirm;

  // Handle focus trapping and keyboard navigation
  useEffect(() => {
    if (isOpen) {
      // Save previously focused element
      previouslyFocused.current = document.activeElement;

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Focus the cancel button by default (safer option)
      setTimeout(() => {
        cancelButtonRef.current?.focus();
      }, 0);

      const handleKeyDown = (e) => {
        // Close on Escape
        if (e.key === 'Escape') {
          e.preventDefault();
          onCancel();
          return;
        }

        // Handle Tab for focus trapping
        if (e.key === 'Tab') {
          const focusableElements = modalRef.current?.querySelectorAll(
            'button:not([disabled])'
          );

          if (!focusableElements || focusableElements.length === 0) return;

          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = '';
        previouslyFocused.current?.focus();
      };
    }
  }, [isOpen, onCancel]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-description"
    >
      <div
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 transform transition-all animate-modal-appear"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`w-16 h-16 rounded-full ${config.iconBg} flex items-center justify-center`}>
            <FontAwesomeIcon
              icon={config.icon}
              className={`${config.iconColor} text-3xl`}
              aria-hidden="true"
            />
          </div>
        </div>

        {/* Title */}
        <h2
          id="confirm-modal-title"
          className="text-xl font-bold text-brand-dark text-center mb-2"
        >
          {title}
        </h2>

        {/* Message */}
        <p
          id="confirm-modal-description"
          className="text-gray-600 text-center mb-6 leading-relaxed"
        >
          {message}
        </p>

        {/* Action buttons */}
        <div className="flex gap-3 justify-center">
          <button
            ref={cancelButtonRef}
            onClick={onCancel}
            className="py-2 px-5 bg-white border border-gray-300 text-gray-700 text-sm rounded-lg font-medium hover:border-brand-teal hover:text-brand-teal transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:ring-offset-2"
          >
            {cancelText}
          </button>
          <button
            ref={confirmButtonRef}
            onClick={onConfirm}
            className={`py-2 px-5 ${config.confirmBg} ${config.confirmText} text-sm rounded-lg font-medium shadow-sm hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal`}
          >
            {confirmText}
          </button>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes modal-appear {
          from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .animate-modal-appear {
          animation: modal-appear 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default ConfirmModal;
